import { getApiOrigin } from '@/config/env';
import { getToken } from '@/utils/auth';

export type AdminUserRow = {
  id: string;
  username: string;
  email: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  isSuperAdmin: boolean;
};

function authHeaders(): HeadersInit {
  return {
    Authorization: `Bearer ${getToken()}`,
    'Content-Type': 'application/json',
  };
}

async function parseJsonError(res: Response, fallback: string): Promise<never> {
  const data = (await res.json().catch(() => ({}))) as { error?: string };
  throw new Error(data.error || fallback);
}

export async function fetchAdminUserList(): Promise<AdminUserRow[]> {
  const apiUrl = getApiOrigin();
  const res = await fetch(`${apiUrl}/api/v1/admin/users`, {
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  const data = (await res.json().catch(() => ({}))) as {
    ok?: boolean;
    users?: AdminUserRow[];
    error?: string;
  };

  if (!res.ok || !data.ok || !Array.isArray(data.users)) {
    throw new Error(data.error || 'Failed to load admin users');
  }

  return data.users;
}

export async function fetchAdminUserById(id: string): Promise<AdminUserRow> {
  const apiUrl = getApiOrigin();
  const res = await fetch(`${apiUrl}/api/v1/admin/users/${encodeURIComponent(id)}`, {
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  const data = (await res.json().catch(() => ({}))) as {
    ok?: boolean;
    user?: AdminUserRow;
    error?: string;
  };

  if (!res.ok || !data.ok || !data.user) {
    await parseJsonError(res, data.error || 'Failed to load admin user');
  }

  return data.user!;
}

export async function createAdminUser(input: {
  username: string;
  email: string;
  name: string;
  password: string;
}): Promise<AdminUserRow> {
  const apiUrl = getApiOrigin();
  const res = await fetch(`${apiUrl}/api/v1/admin/users`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  const data = (await res.json().catch(() => ({}))) as {
    ok?: boolean;
    user?: AdminUserRow;
    error?: string;
  };

  if (!res.ok || !data.ok || !data.user) {
    await parseJsonError(res, data.error || 'Failed to create admin user');
  }

  return data.user!;
}

export async function updateAdminUser(
  id: string,
  input: {
    name?: string;
    email?: string;
    password?: string;
  }
): Promise<AdminUserRow> {
  const apiUrl = getApiOrigin();
  const res = await fetch(`${apiUrl}/api/v1/admin/users/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  const data = (await res.json().catch(() => ({}))) as {
    ok?: boolean;
    user?: AdminUserRow;
    error?: string;
  };

  if (!res.ok || !data.ok || !data.user) {
    await parseJsonError(res, data.error || 'Failed to update admin user');
  }

  return data.user!;
}

export async function deleteAdminUser(id: string): Promise<void> {
  const apiUrl = getApiOrigin();
  const res = await fetch(`${apiUrl}/api/v1/admin/users/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  const data = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string };

  if (!res.ok || !data.ok) {
    await parseJsonError(res, data.error || 'Failed to delete admin user');
  }
}
